import { Edit } from "@mui/icons-material";
import {
  Checkbox,
  IconButton,
  MenuItem,
  TextField,
  Typography,
} from "@mui/material";
import { format } from "date-fns";
import { useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { useUpdateProductMutation } from "src/api/services/productsApi";
import { Column, Product, ProductStatus } from "src/api/types";
import { theme } from "src/assets/theme";
import BillingModel from "src/components/BillingModel";
import { useSelection } from "src/context/ItemsSelectionProvider/useSelection";
import { useSentryLogger } from "src/hooks/useSentryLogger";

const statuses: ProductStatus[] = ["draft", "live", "canceled"];

export const useColumns = (
  onEdit: (item: Product | null) => void,
): Column<Product>[] => {
  const [updateProduct] = useUpdateProductMutation();
  const errorLog = useSentryLogger("error");
  const { selected, setSelected } = useSelection();

  const patientAppUrl = import.meta.env.VITE_REDIRECT_LINK;

  const handleStatus = useCallback(
    async (value: string, row: Product) => {
      try {
        await updateProduct({
          id: row.id,
          status: value as ProductStatus,
          launched_at: value === "live" ? new Date().toISOString() : undefined,
        });
      } catch (err) {
        errorLog("Unable to update product status", err);
      }
    },
    [errorLog, updateProduct],
  );

  const handleSelect = useCallback(
    (id: number) => {
      setSelected(
        selected.includes(id)
          ? selected.filter((s) => s !== id)
          : [...selected, id],
      );
    },
    [selected, setSelected],
  );

  const isSelected = useCallback(
    (id: number) => selected.includes(id),
    [selected],
  );

  return useMemo(
    () => [
      {
        id: "edit",
        label: "Edit",
        minWidth: 80,
        align: "center",
        orderable: false,
        format: (row) => row.id.toString(),
        render: (row) => (
          <IconButton onClick={() => onEdit(row)}>
            <Edit sx={{ color: theme.palette.secondary.main }} />
          </IconButton>
        ),
      },
      {
        id: "checkbox",
        label: "Select",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.id.toString(),
        render: (row) => (
          <Checkbox
            color="primary"
            aria-label="select task"
            onChange={() => handleSelect(row.id)}
            checked={isSelected(row.id)}
          />
        ),
      },
      {
        id: "id",
        label: "ID",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.id.toString(),
        render: (row) => <Link to={`${row.slug}/prices`}>{row.id}</Link>,
      },
      {
        id: "slug",
        label: "Slug",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.slug,
        render: (row) => <Typography>{row.slug}</Typography>,
      },
      {
        id: "status",
        label: "Status",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.status,
        render: (row) => (
          <TextField
            select
            value={row.status}
            size="small"
            onChange={(e) => handleStatus(e.target.value, row)}
          >
            {statuses.map((status) => (
              <MenuItem key={status} value={status}>
                {status}
              </MenuItem>
            ))}
          </TextField>
        ),
      },
      {
        id: "launched",
        label: "Launched",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => {
          if (row.launched_at == null) {
            return "N/A";
          }

          return format(new Date(row.launched_at), "MM/dd/yyyy");
        },
        render: (row) => {
          if (row.launched_at == null) {
            return <Typography>N/A</Typography>;
          }

          return (
            <Typography>
              {format(new Date(row.launched_at), "MM/dd/yyyy")}
            </Typography>
          );
        },
      },
      {
        id: "name",
        label: "Name",
        minWidth: 70,
        align: "center",
        orderable: false,
        format: (row) => row.name,
        render: (row) => <Typography>{row.name}</Typography>,
      },
      {
        id: "display_name",
        label: "Display name",
        minWidth: 70,
        align: "center",
        orderable: false,
        format: (row) => row.display_name ?? "N/A",
        render: (row) => {
          if (row.display_name == undefined) {
            return <Typography>{"N/A"}</Typography>;
          }

          return <Typography>{row.name}</Typography>;
        },
      },
      {
        id: "billing",
        label: "Billing",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.billing_model,
        render: (row) => <BillingModel model={row.billing_model} />,
      },
      {
        id: "category",
        label: "Category",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.category.name,
        render: (row) => <Typography>{row.category.name}</Typography>,
      },
      {
        id: "gender",
        label: "Gender",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.gender ?? "N/A",
        render: (row) => <Typography>{row.gender ?? "N/A"}</Typography>,
      },
      {
        id: "is_glp1",
        label: "Is GLP1",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.is_glp1.toString(),
        render: (row) => <Typography>{row.is_glp1 ? "✔️" : ""}</Typography>,
      },
      {
        id: "insurance_based",
        label: "Insurance Based",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.insurance_based.toString(),
        render: (row) => (
          <Typography>{row.insurance_based ? "✔️" : ""}</Typography>
        ),
      },
      {
        id: "is_e_prescribe",
        label: "Is E-Prescribe",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.is_e_prescribe.toString(),
        render: (row) => (
          <Typography>{row.is_e_prescribe ? "✔️" : ""}</Typography>
        ),
      },
      {
        id: "intake_link",
        label: "Intake link",
        minWidth: 100,
        align: "center",
        orderable: false,
        format: (row) => row.slug,
        render: (row) => (
          <Typography>{`${patientAppUrl}/en/${row.slug}/welcome`}</Typography>
        ),
      },
    ],
    [handleSelect, handleStatus, isSelected, onEdit, patientAppUrl],
  );
};
